// R3 (Rang 5.2, C34 „keine Schriftoption"): Schriftart und Schriftgroesse der
// Chat-Texte (Abenteuer, Begleiter, Tafelrunde, Gruppenchat — alle nutzen
// MessageBubble).
//
// Persistenz ueber clientPrefs (Cookie + localStorage-Write-Through): iOS
// (capacitor://) verliert reine Cookies beim Kaltstart, localStorage bleibt
// (docs/20-gotchas.md, docs/21-design.md). Bewusst NEUE Schluessel statt des
// alten `ff_font`: dort koennen noch Werte der 2026 entfernten Schriftauswahl
// liegen (z. B. „medieval"), die sonst ploetzlich wieder greifen wuerden.
//
// Wirkung: zwei Attribute auf <html> (`data-chat-font`, `data-chat-size`), die
// MessageBubble.module.css auswertet. Das Inline-Script in index.html setzt sie
// schon vor dem React-Mount (kein Springen beim Start).
import { useSyncExternalStore } from 'react';
import { readPref, writePref, type PrefStorage } from '../shared/clientPrefs';

export const CHAT_FONT_PREF = 'ff_chat_font';
export const CHAT_TEXT_SIZE_PREF = 'ff_chat_text_size';

export const CHAT_FONTS = ['default', 'serif', 'sans'] as const;
export type ChatFont = (typeof CHAT_FONTS)[number];

export const CHAT_TEXT_SIZES = ['small', 'normal', 'large', 'xlarge'] as const;
export type ChatTextSize = (typeof CHAT_TEXT_SIZES)[number];

export interface ChatTextPrefs {
  font: ChatFont;
  size: ChatTextSize;
}

export const DEFAULT_CHAT_TEXT_PREFS: ChatTextPrefs = { font: 'default', size: 'normal' };

const CHANGE_EVENT = 'lorath:chat-text-prefs';

function isChatFont(value: unknown): value is ChatFont {
  return typeof value === 'string' && (CHAT_FONTS as readonly string[]).includes(value);
}

function isChatTextSize(value: unknown): value is ChatTextSize {
  return typeof value === 'string' && (CHAT_TEXT_SIZES as readonly string[]).includes(value);
}

export function readChatTextPrefs(cookieSource?: string, storage?: PrefStorage | null): ChatTextPrefs {
  let font: string | null = null;
  let size: string | null = null;
  try {
    font = readPref(CHAT_FONT_PREF, cookieSource, storage);
    size = readPref(CHAT_TEXT_SIZE_PREF, cookieSource, storage);
  } catch {
    // Ohne document/Storage gelten die Standards.
  }
  return {
    font: isChatFont(font) ? font : DEFAULT_CHAT_TEXT_PREFS.font,
    size: isChatTextSize(size) ? size : DEFAULT_CHAT_TEXT_PREFS.size,
  };
}

export function applyChatTextPrefs(prefs: ChatTextPrefs): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.chatFont = prefs.font;
  root.dataset.chatSize = prefs.size;
}

// Zwischenspeicher fuer useSyncExternalStore (gleiche Referenz, solange sich
// nichts aendert — sonst rendert React endlos).
let snapshot: ChatTextPrefs | null = null;

function currentSnapshot(): ChatTextPrefs {
  if (!snapshot) snapshot = readChatTextPrefs();
  return snapshot;
}

/** Speichert (kaltstartfest), wendet sofort an und benachrichtigt alle Leser. */
export function setChatTextPrefs(next: Partial<ChatTextPrefs>, storage?: PrefStorage | null): ChatTextPrefs {
  const base = currentSnapshot();
  const merged: ChatTextPrefs = {
    font: isChatFont(next.font) ? next.font : base.font,
    size: isChatTextSize(next.size) ? next.size : base.size,
  };
  if (merged.font !== base.font) writePref(CHAT_FONT_PREF, merged.font, storage);
  if (merged.size !== base.size) writePref(CHAT_TEXT_SIZE_PREF, merged.size, storage);
  snapshot = merged;
  applyChatTextPrefs(merged);
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGE_EVENT));
  return merged;
}

/** Beim App-Start: gespeicherte Wahl lesen und anwenden. */
export function initChatTextPrefs(): ChatTextPrefs {
  snapshot = readChatTextPrefs();
  applyChatTextPrefs(snapshot);
  return snapshot;
}

/** Nur fuer Tests: Zwischenspeicher verwerfen. */
export function resetChatTextPrefsCacheForTests(): void {
  snapshot = null;
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => window.removeEventListener(CHANGE_EVENT, onChange);
}

export function useChatTextPrefs(): ChatTextPrefs {
  return useSyncExternalStore(subscribe, currentSnapshot, () => DEFAULT_CHAT_TEXT_PREFS);
}
